import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {
  getLengthOfStay,
  getNumberOfRooms,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {Offer} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {Price} from '@vio/shared/src/search/Price'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'
import {loyaltyMessages} from '@vio/shared/src/whiteLabel/loyaltyMessages'
import {
  PointsPrice,
  usePointsPriceValues
} from '@vio/shared/src/whiteLabel/PointsPrice'

import {priceSuffixMessages} from '../../Rooms/RoomOfferPrice/constants'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s300};
  width: 100%;
`

const MainPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${cssTheme.layout.spacing.s200};
`

const PriceText = styled(Text)`
  white-space: nowrap;
`

const DetailsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  width: 100%;
`

interface Props {
  hotelId: string
  offer: Offer
}

export const OtaOfferPrice = ({hotelId, offer}: Props) => {
  const {formatMessage} = useIntl()
  const {isMobile} = useDeviceLayout()
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const numberOfNights = useSelector(getLengthOfStay)
  const showTotalPrices = useSelector(getShowTotalPrices)
  const points = usePointsPriceDisplay()
  const pointsPriceValues = usePointsPriceValues()

  const displayPrice = getDisplayPrice(offer.prices || [])
  const nightlyValue = displayPrice?.chargeable?.nightlyDisplayTotal
  const totalValue = getTotalPrice(displayPrice)
  const currencyCode = displayPrice?.currencyCode

  if (!nightlyValue || !currencyCode) return null

  const mainPriceValue = showTotalPrices
    ? totalValue
    : nightlyValue * numberOfRooms

  return (
    <Wrapper data-id="OtaOfferPrice">
      {points.isOn ? (
        <PointsPrice
          {...points.splitParts(mainPriceValue)}
          currencyCode={currencyCode}
          showMaxPointsCaption={!isMobile}
          alignment="start"
          suffix={
            !showTotalPrices && (
              <Text
                tag="span"
                variant="bodyS"
                colorPath="content.neutral.primary"
              >
                {formatMessage(priceSuffixMessages.perNight)}
              </Text>
            )
          }
        />
      ) : (
        <MainPriceRow>
          <PriceText
            variant="titleL"
            colorPath="content.neutral.primary"
            tag="span"
          >
            <Price currency={currencyCode} value={mainPriceValue} noStyle />
          </PriceText>
          {!showTotalPrices && (
            <PriceText
              variant="bodyS"
              colorPath="content.neutral.primary"
              tag="span"
            >
              {formatMessage(priceSuffixMessages.perNight)}
            </PriceText>
          )}
        </MainPriceRow>
      )}
      <Text tag="div" variant="bodyS" colorPath="content.neutral.primary">
        {points.isOn && !showTotalPrices ? (
          <div>
            <FormattedMessageWrapper
              {...loyaltyMessages.pointsPriceTotalForStay}
              values={{
                ...pointsPriceValues({
                  ...points.splitParts(totalValue),
                  currencyCode
                }),
                rooms: numberOfRooms,
                nights: numberOfNights
              }}
            />
          </div>
        ) : (
          <DetailsRow>
            {!showTotalPrices && (
              <Price currency={currencyCode} value={totalValue} noStyle />
            )}
            <span>{formatMessage(priceSuffixMessages.total)}</span>
            <span>
              {formatMessage(priceSuffixMessages.numberOfRoomsMessage, {
                count: numberOfRooms
              })}
              {', '}
              {formatMessage(priceSuffixMessages.numberOfNightsMessage, {
                count: numberOfNights
              })}
            </span>
          </DetailsRow>
        )}
        {formatMessage(priceSuffixMessages.withTaxesAndFees)}
      </Text>
    </Wrapper>
  )
}
